import React, { useContext, useMemo } from 'react';
import propTypes from 'prop-types';
import { ExperienceContext } from '@thd-nucleus/experience-context';
import { extend } from '@thd-nucleus/data-sources';
import { ListSettings, EmailList } from '@thd-olt-component-react/list-forms';
import { Card, CardTitle, CardBody, Typography } from '@one-thd/sui-atomic-components';
import { ProductListContext } from '../ProductListContext';
import { PrintButton } from '../action-bar/print';

export const ListHeader = ({ list, groupTotals }) => {
  const { channel } = useContext(ExperienceContext);
  const isMobile = channel === 'mobile';

  const {
    isSharedList,
    onDelete,
    onEditSuccess
  } = useContext(ProductListContext);

  const {
    id, itemCount, name
  } = list;

  // Calculate subtotal from groupTotals (project-details pattern)
  const subTotal = useMemo(() => {
    if (!groupTotals || Object.keys(groupTotals).length === 0) {
      return 0;
    }
    const groupTotalsValues = Object.values(groupTotals);
    if (groupTotalsValues.includes(null)) {
      return 0;
    }
    return groupTotalsValues.reduce((acc, total) => acc + total, 0);
  }, [groupTotals]);

  const priceFormatter = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD'
  });
  const formattedPrice = priceFormatter.format(subTotal ?? 0).split('.');
  const priceWithoutSign = formattedPrice[0].slice(1);
  const decimals = formattedPrice[1];

  const isEmptyList = itemCount === 0;

  const onSettingsSave = () => {
    onEditSuccess({});
  };
  const onDeleteList = () => {
    if (onDelete) {
      onDelete();
    }
  };

  return (
    <div className="sui-grid sui-gap-3 sui-mr-2 sui-ml-2">
      {isMobile && (
        <div className="sui-w-full sui-py-2 sui-flex sui-gap-2 sui-justify-end">
          {(!isSharedList && !!id) && (
            <>
              <ListSettings
                iconSize="regular"
                list={list}
                onClose={() => { }}
                onSave={onSettingsSave}
                onDelete={onDeleteList}
                isEmptyList={isEmptyList}
              />

              {(!!itemCount) && (
                <EmailList
                  iconSize="regular"
                  listId={id}
                  list={list}
                  shareIcon
                />
              )}
            </>
          )}
        </div>
      )}
      <Card className="sui-w-full">
        <CardTitle header={(
          <div data-testid="ListNameTitleContainer" className="sui-break-all">
            <Typography variant="h2" weight="display">{name}</Typography>
          </div>
        )}
        />
        <CardBody>
          <div className="sui-flex sui-justify-between sui-items-center">
            <div className="sui-flex sui-gap-8">
              <div className="sui-flex sui-flex-col sui-gap-2">
                <div className="sui-flex sui-items-center sui-gap-1">
                  <Typography variant="h5" weight="display">$</Typography>
                  <Typography variant="h2" weight="display">{priceWithoutSign}</Typography>
                  <Typography variant="h5" weight="display">{decimals}</Typography>
                </div>
                <Typography align="center" variant="body-base" weight="bold">Subtotal</Typography>
              </div>
              <div className="sui-flex sui-flex-col sui-gap-2">
                <Typography align="center" variant="h2" weight="display">{itemCount || 0}</Typography>
                <Typography align="center" variant="body-base" weight="bold">Items</Typography>
              </div>
            </div>
            {!isMobile && (
              <div className={`sui-grid ${itemCount > 0 ? 'sui-grid-cols-3' : ''} sui-gap-2`}>
                {(!isSharedList && !!id) && (
                  <ListSettings
                    iconSize="regular"
                    list={list}
                    onClose={() => { }}
                    onSave={onSettingsSave}
                    onDelete={onDeleteList}
                    isEmptyList={isEmptyList}
                  />
                )}
                {(!!itemCount) && (
                  <>
                    <PrintButton />
                    {(!isSharedList && !!id) && (
                      <EmailList
                        iconSize="regular"
                        listId={id}
                        list={list}
                        shareIcon
                      />
                    )}
                  </>
                )}
              </div>
            )}
          </div>
        </CardBody>
      </Card>
    </div>
  );
};

ListHeader.propTypes = {
  list: propTypes.shape({
    id: propTypes.string,
    itemCount: propTypes.number,
    name: propTypes.string,
    listAccessType: propTypes.string
  }).isRequired,
  groupTotals: propTypes.shape({
    [propTypes.string]: propTypes.number
  })
};

ListHeader.defaultProps = {
  groupTotals: {}
};

ListHeader.displayName = 'ListHeader';

ListHeader.dataModel = extend(
  ListSettings,
  EmailList
);
